import React, { useState } from 'react';
import axios from 'axios';
import { Stethoscope, AlertTriangle, Loader2, CheckCircle2, ArrowRight, Ambulance } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SYMPTOMS = [
  'fever', 'cough', 'sore_throat', 'headache', 'nausea', 'rash',
  'back_pain', 'diarrhea', 'uti_symptoms', 'ear_pain', 'abdominal_pain',
  'joint_swelling', 'shortness_of_breath', 'dizziness', 'blood_in_stool',
  'vomiting_blood', 'fainting',
];

const RED_FLAGS = [
  { key: 'chest_pain', label: 'Chest pain or pressure' },
  { key: 'stroke_signs', label: 'Stroke signs (face droop, slurred speech)' },
  { key: 'severe_bleeding', label: 'Severe uncontrolled bleeding' },
  { key: 'trouble_breathing', label: 'Severe trouble breathing' },
  { key: 'suicide_intent', label: 'Thoughts of harming yourself' },
  { key: 'anaphylaxis', label: 'Severe allergic reaction (anaphylaxis)' },
  { key: 'head_trauma_loc', label: 'Head trauma with loss of consciousness' },
];

const LEVEL_COLOR = {
  ER: 'bg-red-100 text-red-900 border-red-300',
  urgent_visit: 'bg-amber-100 text-amber-900 border-amber-300',
  telehealth: 'bg-blue-100 text-blue-900 border-blue-300',
  self_care: 'bg-emerald-100 text-emerald-900 border-emerald-300',
};

const LEVEL_ICON = {
  ER: Ambulance, urgent_visit: AlertTriangle,
  telehealth: Stethoscope, self_care: CheckCircle2,
};

export default function PatientTeleTriage() {
  const { getAuthHeader } = useAuth();
  const [symptoms, setSymptoms] = useState([]);
  const [pain, setPain] = useState(0);
  const [age, setAge] = useState('');
  const [pregnant, setPregnant] = useState(false);
  const [redFlags, setRedFlags] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const toggle = (s) => setSymptoms(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const flagToggle = (k) => setRedFlags(prev => ({ ...prev, [k]: !prev[k] }));

  const submit = async () => {
    if (!symptoms.length) {
      toast.error('Select at least one symptom'); return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/triage/assess`, {
        symptoms, pain_scale: pain || null, age: age ? parseInt(age) : null,
        pregnant, red_flags: redFlags,
      }, getAuthHeader());
      setResult(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run triage');
    } finally { setSubmitting(false); }
  };

  const reset = () => { setSymptoms([]); setPain(0); setAge(''); setPregnant(false); setRedFlags({}); setResult(null); };

  const LevelIcon = result ? LEVEL_ICON[result.level] : null;

  return (
    <div className="min-h-screen bg-cn-bone-50 px-4 sm:px-8 py-10">
      <div className="max-w-3xl mx-auto" data-testid="patient-tele-triage-page">
        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-cn-text mb-2">Tele-Triage</h1>
          <p className="text-cn-text-muted text-sm">Tell us how you feel. We&apos;ll route you to the right level of care in under a minute.</p>
        </div>

        {!result && (
          <div className="cn-card p-6 space-y-6" data-testid="triage-form">
            <div>
              <h3 className="font-display font-semibold text-base mb-2">1. What symptoms are you experiencing?</h3>
              <div className="flex flex-wrap gap-2" data-testid="triage-symptoms">
                {SYMPTOMS.map(s => (
                  <button
                    key={s}
                    onClick={() => toggle(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${symptoms.includes(s)
                      ? 'bg-cn-primary text-white border-cn-primary'
                      : 'bg-white text-cn-text-muted border-cn-border hover:border-cn-primary'
                      }`}
                    data-testid={`triage-symptom-${s}`}
                  >
                    {s.replace(/_/g, ' ')}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-cn-text-muted">Age</label>
                <input type="number" value={age} onChange={e => setAge(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-cn-border rounded-lg text-sm"
                  data-testid="triage-age" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-cn-text-muted">Pain scale (0–10)</label>
                <input type="range" min="0" max="10" value={pain} onChange={e => setPain(parseInt(e.target.value))}
                  className="w-full mt-2" data-testid="triage-pain" />
                <p className="text-xs text-cn-text-muted">Current: <span className="font-semibold">{pain}</span></p>
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm" data-testid="triage-pregnant-label">
              <input type="checkbox" checked={pregnant} onChange={e => setPregnant(e.target.checked)} data-testid="triage-pregnant" />
              Currently pregnant
            </label>

            <div>
              <h3 className="font-display font-semibold text-base mb-2">2. Any red-flag symptoms?</h3>
              <div className="space-y-1" data-testid="triage-redflags">
                {RED_FLAGS.map(f => (
                  <label key={f.key} className="flex items-center gap-2 text-sm" data-testid={`triage-redflag-${f.key}-label`}>
                    <input type="checkbox" checked={!!redFlags[f.key]} onChange={() => flagToggle(f.key)} data-testid={`triage-redflag-${f.key}`} />
                    <span className={redFlags[f.key] ? 'font-semibold text-red-700' : 'text-cn-text-muted'}>{f.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <button onClick={submit} disabled={submitting}
              className="w-full cn-btn cn-btn-primary !py-3"
              data-testid="triage-submit">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Run triage'}
            </button>
          </div>
        )}

        {result && (
          <div className={`rounded-2xl border-2 p-6 ${LEVEL_COLOR[result.level]}`} data-testid="triage-result">
            <div className="flex items-center gap-3 mb-4">
              {LevelIcon && <LevelIcon className="w-8 h-8" />}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider opacity-70">Recommended level of care</p>
                <h2 className="font-display font-bold text-2xl capitalize" data-testid="triage-result-level">{result.level.replace('_', ' ')}</h2>
              </div>
            </div>
            <p className="text-base mb-4" data-testid="triage-result-recommendation">{result.recommendation}</p>
            {(result.rules_fired || []).length > 0 && (
              <div className="mb-4">
                <p className="text-[11px] uppercase tracking-wider font-bold opacity-70 mb-1">Why we recommended this</p>
                <ul className="text-xs space-y-0.5">
                  {result.rules_fired.map((r, i) => (
                    <li key={i} className="flex items-center gap-1">
                      <ArrowRight className="w-3 h-3" /> {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.spawned_referral_id && (
              <p className="text-xs bg-white/70 rounded-lg p-3" data-testid="triage-result-referral">
                We&apos;ve automatically created a priority referral so your care team can act fast.
              </p>
            )}
            <button onClick={reset} className="mt-4 cn-btn cn-btn-ghost !py-2" data-testid="triage-reset">
              Start over
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
